import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getConfig, isProduction } from '../../config/appConfig';
import {
  SALES_EMAIL,
  sendSalesEmail,
  buildCustomPlanMailtoBody,
} from '../../utils/salesContact';
import toast from 'react-hot-toast';
import { apiUrl } from '../../utils/apiBase';
import apiService from '../../services/apiService';
import { getAuthApiKey, getAuthUser, candorWebRequestAuth, hasCookieSession } from '../../utils/authTokenStore';
import paddleService from '../../services/paddleService';
import { useSubscription } from '../../context/SubscriptionContext';
import planService from '../../services/planService';
import PricingPlansGrid from './PricingPlansGrid';
import {
  resolveCurrentPlanSlug,
  developerTierKey,
  getCheckoutPrice,
  groupPlansForPricingGrid,
} from '../../utils/planCatalogUtils';
import {
  getCheckoutReturnParams,
  storeCheckoutContext,
  normalizeBillingCycle,
} from '../../utils/checkoutReturnParams';
import TokenUsageDisplay from './TokenUsageDisplay';
import CompliancePolicyModal from '../../components/common/CompliancePolicyModal';
import CancelModal from './CancelModal';
import PlanSwitchWarningModal from './PlanSwitchWarningModal';
import { PRICING_HERO, TRUSTED_PAYMENT } from '../../data/pricingCatalog';

const PLAN_SWITCH_WARNING = 'Switching from Plan Reset the Current tokens so Use the tokens before switching to another Plan';

function initialBillingIntervalFromUrl() {
  try {
    if (typeof window === 'undefined') return 'monthly';
    const search = new URLSearchParams(window.location.search || '');
    let raw = search.get('billing_cycle') || search.get('billing_interval') || search.get('interval');
    if (!raw && window.location.hash && window.location.hash.indexOf('?') !== -1) {
      const hp = new URLSearchParams(window.location.hash.split('?')[1] || '');
      raw = hp.get('billing_cycle') || hp.get('billing_interval') || hp.get('interval');
    }
    return normalizeBillingCycle(raw || 'monthly');
  } catch (_) {
    return 'monthly';
  }
}

const SubscriptionPlans = () => {
  const { subscription, refreshData, cancelSubscription } = useSubscription();
  const [user, setUser] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [plansList, setPlansList] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [showPlanSwitchModal, setShowPlanSwitchModal] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState(null);
  
  // ***** Code Start BH10092026_1415 - Honor ?billing_cycle=yearly from website Pricing
  const [billingInterval, setBillingInterval] = useState(initialBillingIntervalFromUrl);
  // ***** Code End BH10092026_1415
  const [checkoutMode] = useState('paddle_sdk');
  const [developerTab, setDeveloperTab] = useState('basic'); // 'basic', 'plus', 'ultimate'
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);
  const [earlyAccessTier] = useState('Developer');
  const [activeComplianceModal, setActiveComplianceModal] = useState(null); // 'privacy', 'terms', 'refund', 'disclaimer'

  // Mock Checkout Modal State
  const [showMockCheckout, setShowMockCheckout] = useState(false);
  const [mockCheckoutPlan, setMockCheckoutPlan] = useState(null); // { planId, planName, price, sessionId }
  const [mockCheckoutProcessing, setMockCheckoutProcessing] = useState(false);
  const [mockCheckoutError, setMockCheckoutError] = useState(null);
  const [mockCardForm, setMockCardForm] = useState({ number: '', expiry: '', cvv: '', name: '' });
  const [mockCheckoutSuccess, setMockCheckoutSuccess] = useState(false);

  const [loading, setLoading] = useState({
    manage: false,
    cancel: false,
    upgrade: false,
    downgrade: false,
    pro_plan: false,
    developer_plus: false,
    developer_ultimate: false,
    pay_as_you_go: false,
    free_trial: false
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const getUser = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const userParam = urlParams.get('user');
        if (userParam) {
          try {
            const userData = JSON.parse(decodeURIComponent(userParam));
            setUser(userData);
            return;
          } catch (error) {}
        }

        const cdpUserObj = getAuthUser();
        const cdpUser = cdpUserObj ? JSON.stringify(cdpUserObj) : null;
        if (cdpUser) {
          const userData = JSON.parse(cdpUser);
          setUser(userData);
          return;
        }

        const vscodeUser = localStorage.getItem('vscode_user') || localStorage.getItem('candor_vscode_user');
        if (vscodeUser) {
          const userData = JSON.parse(vscodeUser);
          setUser(userData);
          return;
        }

        const apiKey = getAuthApiKey();
        if (apiKey || hasCookieSession()) {
          try {
            const response = await fetch(apiUrl(process.env.REACT_APP_ENDPOINT_USER_PROFILE || '/api/v1/users/profile'), {
              ...candorWebRequestAuth({ 'Content-Type': 'application/json' }),
            });
            
            if (response.ok) {
              const userData = await response.json();
              setUser({
                email: userData.email,
                name: userData.name,
                company: userData.company,
                role: userData.role,
                is_active: userData.is_active
              });
              return;
            }
          } catch (apiError) {}
          setUser(null);
          return;
        }
        setUser(null);
      } catch (error) {
        setUser(null);
      }
    };

    getUser();
  }, []);

  // ***** Code Start BH10092026_1716 - Use TTL cache; force only after checkout return
  // Load plans from database (TTL in planService; prices from subscription_plans.price)
  useEffect(() => {
    const loadPlans = async () => {
      try {
        setPlansLoading(true);
        const checkout = getCheckoutReturnParams();
        const forceRefresh = Boolean(checkout?.hasCheckoutReturn);
        if (forceRefresh) {
          planService.clearPlansCache();
        }
        const list = await planService.getSortedPlansList({ forceRefresh });
        setPlansList(list);
      } catch (error) {
        setPlansList([]);
      } finally {
        setPlansLoading(false);
      }
    };
    loadPlans();
  }, []);
  // ***** Code End BH10092026_1716

  useEffect(() => {
    if (!subscription?.plan_name || !plansList.length) return;
    const { developerTiers } = groupPlansForPricingGrid(plansList);
    const slug = resolveCurrentPlanSlug(subscription, plansList);
    const match = developerTiers.find((p) => p.slug === slug);
    if (match) {
      setDeveloperTab(developerTierKey(match));
    }
  }, [subscription, plansList]);

  // Refresh when returning from checkout (hash or search params)
  useEffect(() => {
    const params = getCheckoutReturnParams();
    if (params.hasCheckoutReturn) {
      const t = setTimeout(() => {
        refreshData().catch(() => {});
      }, 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [refreshData]);

  const startPlanChange = async (planId) => {
    if (!planId) return;

    if (currentPlan === 'pro_plan' && planId === 'pay_as_you_go') {
      setLoading(prev => ({ ...prev, upgrade: true }));
    } else if (currentPlan === 'pay_as_you_go' && planId === 'pro_plan') {
      setLoading(prev => ({ ...prev, downgrade: true }));
    } else {
      setLoading(prev => ({ ...prev, [planId]: true }));
    }
    setError(null);
    setSuccess(null);

    try {
      const plan = plansList.find((p) => p.slug === planId);
      
      if (!plan) {
        throw new Error(`Plan "${planId}" not found. Please refresh the page to load latest plans from database.`);
      }

      if (!plan.name || plan.price === undefined) {
        throw new Error(`Invalid plan data for "${planId}". Database values missing. Please refresh the page.`);
      }

      const buildSuccessUrl = getConfig('paddle.buildSuccessUrl');
      const buildCancelUrl = getConfig('paddle.buildCancelUrl');
      const successUrl = typeof buildSuccessUrl === 'function' ? buildSuccessUrl(planId, billingInterval) : `${window.location.origin}/success?success=true&plan=${planId}&billing_cycle=${billingInterval}&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = typeof buildCancelUrl === 'function' ? buildCancelUrl(planId, billingInterval) : `${window.location.origin}/success?success=false&plan=${planId}&billing_cycle=${billingInterval}`;
      
      const session = await paddleService.createCheckoutSession(
        planId,
        successUrl,
        cancelUrl,
        user.email,
        {
          ...plan,
          billing_interval: billingInterval
        }
      );
      
      const transactionId = session.transaction_id || session.session_id;

      storeCheckoutContext({
        sessionId: transactionId,
        planId,
        billingCycle: billingInterval,
      });

      // Real Paddle SDK integration flow
      let sdkFailureReason = null;
      if (checkoutMode === 'paddle_sdk') {
        try {
          const isRealPaddleTxn = transactionId && typeof transactionId === 'string' && transactionId.startsWith('txn_') && !transactionId.includes('mock') && !transactionId.includes('dummy');
          
          // Check if we can fallback to client-side price ID mapping for the Paddle Overlay
          const priceId = paddleService.mapPlanIdToPriceId(planId, billingInterval, plan);
          const hasPriceId = priceId && typeof priceId === 'string' && priceId.startsWith('pri_');
          
          if (!isRealPaddleTxn && !hasPriceId) {
            // ***** BH29072026_1700 — name the missing price var; this is what breaks Plus/Ultimate
            throw new Error(`No Paddle price is configured for "${planId}" (${billingInterval}), and the backend returned a non-Paddle transaction id "${transactionId}". Set REACT_APP_PADDLE_PRICE_* for this plan in .env.production and PADDLE_API_KEY / PADDLE_ENVIRONMENT for llm_server.`);
          }
          await paddleService.redirectToCheckout(transactionId, session.url, successUrl, cancelUrl, planId, billingInterval, plan);
          return; // Successfully opened the native Paddle checkout overlay!
        } catch (sdkError) {
          sdkFailureReason = sdkError.message || 'Failed to initialize the Paddle SDK.';
          setError(sdkFailureReason);
        }
      }

      // ***** Code Start BH29072026_1700 - Never collect card details outside Paddle in production
      // The in-app form below is a development stand-in. Showing it on the live site looks like
      // a real checkout, is not PCI compliant, and hides the underlying Paddle misconfiguration.
      if (isProduction()) {
        throw new Error(
          `Could not open the Paddle checkout, so no payment was started. ${sdkFailureReason || ''} `.trim() +
            ' Please do not enter card details anywhere else — this needs a Paddle configuration fix.'
        );
      }
      // ***** Code End BH29072026_1700

      // Open our unified, high-fidelity in-app checkout page with card inputs for all plans
      const { price: checkoutPrice, interval: checkoutInterval } = getCheckoutPrice(
        plan,
        billingInterval
      );

      setMockCheckoutPlan({
        planId,
        planName: plan.name,
        price: checkoutPrice,
        interval: checkoutInterval,
        sessionId: transactionId,
        successUrl
      });
      setMockCardForm({ number: '', expiry: '', cvv: '', name: '' });
      setMockCheckoutError(null);
      setShowMockCheckout(true);
      
    } catch (error) {
      const detail =
        error?.data?.detail ||
        (typeof error?.data === 'string' ? error.data : null);
      setError(
        error?.message ||
          (typeof detail === 'string' ? detail : null) ||
          'Failed to change subscription plan'
      );
    } finally {
      if (currentPlan === 'pro_plan' && planId === 'pay_as_you_go') {
        setLoading(prev => ({ ...prev, upgrade: false }));
      } else if (currentPlan === 'pay_as_you_go' && planId === 'pro_plan') {
        setLoading(prev => ({ ...prev, downgrade: false }));
      } else {
        setLoading(prev => ({ ...prev, [planId]: false }));
      }
    }
  };

  // Handle mock checkout form submission — activates plan via backend
  const handleMockCheckoutSubmit = async (e) => {
    e.preventDefault();
    if (!mockCheckoutPlan) return;

    // Basic card validation
    const rawNumber = mockCardForm.number.replace(/\s/g, '');
    if (rawNumber.length < 13 || rawNumber.length > 19) {
      setMockCheckoutError('Please enter a valid card number (13–19 digits).');
      return;
    }
    if (!mockCardForm.expiry.match(/^(0[1-9]|1[0-2])\/\d{2}$/)) {
      setMockCheckoutError('Expiry must be in MM/YY format.');
      return;
    }
    if (mockCardForm.cvv.length < 3) {
      setMockCheckoutError('Please enter a valid CVV (3–4 digits).');
      return;
    }
    if (!mockCardForm.name.trim()) {
      setMockCheckoutError('Please enter the cardholder name.');
      return;
    }

    setMockCheckoutProcessing(true);
    setMockCheckoutError(null);

    try {
      const apiKey = getAuthApiKey();

      const headers = candorWebRequestAuth({ 'Content-Type': 'application/json' }).headers;

      // Step 1: verify via apiService (cs_mock_* → verify-checkout-session)
      let activated = false;
      try {
        const verifyData = await apiService.verifyCheckoutSession({
          sessionId: mockCheckoutPlan.sessionId,
          planId: mockCheckoutPlan.planId,
          billingCycle: mockCheckoutPlan.interval === 'year' ? 'yearly' : 'monthly',
        });
        if (verifyData?.verified || verifyData?.success || verifyData?.status === 'success') {
          activated = true;
        }
      } catch (_) {}

      // Step 2: If verify didn't activate, use direct upgrade endpoint
      if (!activated) {
        const upgradeResponse = await fetch(apiUrl(process.env.REACT_APP_ENDPOINT_SUBSCRIPTION_UPGRADE || '/api/v1/subscriptions/upgrade'), {
          method: 'POST',
          headers,
          body: JSON.stringify({ 
            plan_name: mockCheckoutPlan.planName,
            billing_cycle: mockCheckoutPlan.interval === 'year' ? 'yearly' : 'monthly'
          })
        });
        const upgradeData = await upgradeResponse.json();
        // Upgrade returns the subscription object on success, or an error
        if (upgradeResponse.ok && !upgradeData.detail) {
          activated = true;
        } else if (!upgradeResponse.ok) {
          setMockCheckoutError(upgradeData.detail || upgradeData.message || 'Upgrade failed. Please contact support.');
          return;
        } else {
          activated = true; // Response is ok even with message
        }
      }

      if (activated) {
        setMockCheckoutSuccess(true);
        // Silently refresh the subscription details in the background immediately
        refreshData().catch(() => {});
      } else {
        setMockCheckoutError('Could not activate plan. Please try again or contact support.');
      }
    } catch (err) {
      setMockCheckoutError('Network error. Please check your connection and try again.');
    } finally {
      setMockCheckoutProcessing(false);
    }
  };

  // Format card number with spaces every 4 digits
  const formatCardNumber = (value) => {
    return value.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim().slice(0, 19);
  };

  // Format expiry as MM/YY
  const formatExpiry = (value) => {
    const clean = value.replace(/\D/g, '').slice(0, 4);
    if (clean.length >= 3) return `${clean.slice(0, 2)}/${clean.slice(2)}`;
    return clean;
  };

  const handlePlanChange = async (planId) => {
    if (!user) {
      setError('Please wait for user authentication to complete');
      return;
    }

    const subscriptionStatus = subscription?.status?.toLowerCase();
    const isSwitchingActivePlan = subscriptionStatus === 'active' && currentPlan && currentPlan !== planId;
    if (isSwitchingActivePlan) {
      setPendingPlanId(planId);
      setShowPlanSwitchModal(true);
      return;
    }

    await startPlanChange(planId);
  };

  const confirmPlanSwitch = async () => {
    const targetPlan = pendingPlanId;
    setShowPlanSwitchModal(false);
    setPendingPlanId(null);
    if (targetPlan) {
      await startPlanChange(targetPlan);
    }
  };

  const handleCancelSubscription = () => {
    if (!user) {
      setError('Please log in to cancel your subscription');
      return;
    }
    setShowCancelModal(true);
  };

  const confirmCancelSubscription = async () => {
    setLoading(prev => ({ ...prev, cancel: true }));
    setError(null);
    setSuccess(null);

    try {
      await cancelSubscription();
      setShowCancelModal(false);
      setSuccess(
        'Subscription cancelled successfully. You will retain access until the end of your billing period.'
      );
      await refreshData();
    } catch (err) {
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setLoading(prev => ({ ...prev, cancel: false }));
    }
  };

  const currentPlan = resolveCurrentPlanSlug(subscription, plansList);

  const openContactSales = (options = {}) => {
    const subject = options.subject || 'Corporate Plan — Enterprise inquiry';
    sendSalesEmail({
      subject,
      body: buildCustomPlanMailtoBody(user?.email),
    });
    toast.success(`Opening your email app — ${SALES_EMAIL}`, { duration: 3000 });
    setSuccess(`Email opened for ${SALES_EMAIL}. Send the message to reach our sales team.`);
  };

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading user authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="candor-pricing-page -mx-2 sm:-mx-0">

      {/* ====== MOCK CHECKOUT MODAL ====== */}
      {showMockCheckout && mockCheckoutPlan && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(8px)' }}>
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 pt-6 pb-5 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <span className="text-sm font-bold text-slate-300">Secure Checkout</span>
                </div>
                <button
                  onClick={() => { setShowMockCheckout(false); setMockCheckoutError(null); }}
                  className="text-slate-400 hover:text-white transition text-xl leading-none"
                  aria-label="Close"
                >✕</button>
              </div>
              {/* Plan Summary */}
              <div className="bg-white/10 rounded-xl px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">Subscribing to</p>
                  <p className="text-lg font-black text-white mt-0.5">{mockCheckoutPlan.planName}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-black text-white">
                    ${parseFloat(mockCheckoutPlan.price || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-400">
                    {mockCheckoutPlan.interval === 'year' ? '/year' : mockCheckoutPlan.interval === 'month' ? '/month' : ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Card Form or Success View */}
            {mockCheckoutSuccess ? (
              <div className="px-6 py-8 text-center space-y-6 animate-fade-in">
                {/* Bouncing Success Checkmark */}
                <div className="mx-auto w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center border border-emerald-100 shadow-inner">
                  <span className="text-4xl animate-bounce" style={{ display: 'inline-block', animationDuration: '1.5s' }}>✅</span>
                </div>

                <div className="space-y-2">
                  <h3 className="text-2xl font-black text-slate-900 tracking-tight">
                    Payment Successful!
                  </h3>
                  <p className="text-sm text-slate-500 font-medium max-w-xs mx-auto">
                    Your subscription has been successfully upgraded to <span className="text-slate-950 font-bold">{mockCheckoutPlan.planName}</span>.
                  </p>
                </div>

                {/* Plan Details Box */}
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100/80 text-left space-y-2.5">
                  <div className="flex justify-between text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    <span>Transaction Details</span>
                    <span className="text-emerald-600 font-extrabold">Paid</span>
                  </div>
                  <div className="h-px bg-slate-200/50"></div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 font-medium">Plan</span>
                    <span className="text-sm text-slate-800 font-bold">{mockCheckoutPlan.planName}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 font-medium">Amount</span>
                    <span className="text-sm text-slate-800 font-extrabold">
                      ${parseFloat(mockCheckoutPlan.price || 0).toFixed(2)}{mockCheckoutPlan.interval === 'year' ? '/yr' : mockCheckoutPlan.interval === 'month' ? '/mo' : ''}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 font-medium">Billing Period</span>
                    <span className="text-sm text-slate-800 font-medium">
                      {mockCheckoutPlan.interval === 'year' ? 'Yearly' : 'Monthly'} (Auto-renews)
                    </span>
                  </div>
                </div>

                <button
                  onClick={async () => {
                    setShowMockCheckout(false);
                    setMockCheckoutSuccess(false);
                    setMockCheckoutError(null);
                    await refreshData();
                  }}
                  className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl text-sm transition duration-200 shadow-lg shadow-emerald-100 hover:shadow-emerald-200 flex items-center justify-center gap-2"
                >
                  <span>Done &amp; Refresh Dashboard</span>
                  <span>→</span>
                </button>
              </div>
            ) : (
              <form onSubmit={handleMockCheckoutSubmit} className="px-6 py-5 space-y-4">
                {mockCheckoutError && (
                  <div className="bg-rose-50 border border-rose-200 rounded-2xl px-4 py-3.5 text-sm text-rose-800 font-semibold flex items-start gap-3 shadow-sm">
                    <span className="text-lg leading-none mt-0.5">❌</span>
                    <div className="space-y-1">
                      <p className="font-extrabold text-rose-900">Payment Failed</p>
                      <p className="text-xs text-rose-700 font-normal leading-relaxed">{mockCheckoutError}</p>
                    </div>
                  </div>
                )}

                {/* Card Number */}
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Card Number</label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="4242 4242 4242 4242"
                      value={mockCardForm.number}
                      onChange={e => setMockCardForm(f => ({ ...f, number: formatCardNumber(e.target.value) }))}
                      className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition pr-12"
                      maxLength={19}
                      required
                    />
                    <svg className="absolute right-3 top-3.5 w-5 h-5 text-slate-300" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm0 5v7h16V9H4zm2 4h4v2H6v-2z"/>
                    </svg>
                  </div>
                </div>

                {/* Expiry + CVV */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Expiry (MM/YY)</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="12/28"
                      value={mockCardForm.expiry}
                      onChange={e => setMockCardForm(f => ({ ...f, expiry: formatExpiry(e.target.value) }))}
                      className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      maxLength={5}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">CVV</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="123"
                      value={mockCardForm.cvv}
                      onChange={e => setMockCardForm(f => ({ ...f, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                      className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      maxLength={4}
                      required
                    />
                  </div>
                </div>

                {/* Cardholder Name */}
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Cardholder Name</label>
                  <input
                    type="text"
                    placeholder="Sathsih"
                    value={mockCardForm.name}
                    onChange={e => setMockCardForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    required
                  />
                </div>

                {/* Trust Badges */}
                <div className="flex items-center gap-4 py-1">
                  <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
                    <svg className="w-3.5 h-3.5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                    <span>256-bit SSL</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
                    <svg className="w-3.5 h-3.5 text-blue-500" fill="currentColor" viewBox="0 0 20 20"><path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/><path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/></svg>
                    <span>Secure via Paddle</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
                    <span className="text-green-500">✓</span>
                    <span>30-Day Guarantee</span>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={mockCheckoutProcessing}
                  className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-black rounded-xl text-sm transition duration-200 shadow-lg hover:shadow-blue-200 flex items-center justify-center gap-2"
                >
                  {mockCheckoutProcessing ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>🔒 Pay ${parseFloat(mockCheckoutPlan.price || 0).toFixed(2)} &amp; Upgrade Now</>
                  )}
                </button>
                <p className="text-center text-xs text-slate-400">
                  You can cancel anytime from your account settings.
                </p>
              </form>
            )}
          </div>
        </div>,
        document.body
      )}

      <section className="candor-pricing-hero">
        <div className="candor-pricing-hero-inner">
          <div className="candor-pricing-hero-block">
            <p className="candor-pricing-kicker">{PRICING_HERO.kicker}</p>
            <h1 className="candor-pricing-headline">{PRICING_HERO.headline}</h1>
            <p className="candor-pricing-lead">{PRICING_HERO.lead}</p>
          </div>
          <div className="candor-pricing-hero-block candor-pricing-hero-block--tight">
            <p className="candor-pricing-meta">{PRICING_HERO.usdNotice}</p>
          </div>
          <div className="candor-pricing-hero-block candor-pricing-hero-block--tight">
            <ul className="candor-trust-row" aria-label="Pricing guarantees">
              {PRICING_HERO.trustChips.map((chip) => (
                <li key={chip.id}>
                  <span className="candor-trust-chip">{chip.label}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="candor-pricing-hero-block candor-pricing-hero-block--tight">
            <p className="candor-pricing-footnote">{PRICING_HERO.footnote}</p>
          </div>
          <div className="candor-pricing-billing-wrap">
            <div className="candor-segment" role="tablist" aria-label="Billing interval">
              <button
                type="button"
                role="tab"
                aria-selected={billingInterval === 'monthly'}
                className={`candor-segment-btn ${billingInterval === 'monthly' ? 'is-active' : ''}`}
                onClick={() => setBillingInterval('monthly')}
              >
                Monthly
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={billingInterval === 'yearly'}
                className={`candor-segment-btn candor-segment-btn--yearly ${billingInterval === 'yearly' ? 'is-active' : ''}`}
                onClick={() => setBillingInterval('yearly')}
              >
                Yearly
                <span className="candor-segment-discount" aria-label="20 percent off yearly billing">
                  {PRICING_HERO.yearlyDiscountBadge}
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION Banners & Alerts */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {error && (
          <div className="candor-alert candor-alert--error" role="alert">
            <strong>Error</strong>
            <p>{error}</p>
          </div>
        )}

        {success && (
          <div className="candor-alert candor-alert--success" role="status">
            <strong>Success</strong>
            <p>{success}</p>
          </div>
        )}

        {subscription?.status?.toLowerCase() === 'expired' && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-5 flex gap-4 shadow-sm">
            <span className="text-red-500 text-2xl flex-shrink-0 mt-0.5">🚨</span>
            <div>
              <h3 className="text-lg font-bold text-red-800">Subscription Expired</h3>
              <p className="text-sm text-red-700 mt-1">
                {subscription.message || "Your subscription has expired. Please choose a plan to continue using the service."}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* SECTION Pricing Grid — loaded from API (is_active, plan_sort) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PricingPlansGrid
          plansList={plansList}
          plansLoading={plansLoading}
          currentPlanSlug={currentPlan}
          subscription={subscription}
          billingInterval={billingInterval}
          developerTab={developerTab}
          onDeveloperTabChange={setDeveloperTab}
          onPlanSelect={handlePlanChange}
          onCancelSubscription={handleCancelSubscription}
          onContactSales={() => openContactSales()}
          loading={loading}
        />

        <aside className="candor-pricing-disclaimers" aria-label="Billing notes">
          <p>{PRICING_HERO.taxNote}</p>
          <p>{PRICING_HERO.paddleB2bNote}</p>
        </aside>
      </div>

      {/* Token Usage Section for Current Plan */}
      {currentPlan && subscription && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 w-full">
          <div className="bg-gradient-to-br from-indigo-50/70 to-blue-50/70 border border-indigo-100 rounded-3xl p-5 sm:p-6 shadow-sm w-full">
            <header className="mb-5">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <span className="text-xl" aria-hidden>📊</span>
                Active plan usage status
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Base subscription tokens and add-on packs are tracked separately below.
              </p>
            </header>
            <TokenUsageDisplay subscription={subscription} />
          </div>
        </div>
      )}

      {/* SECTION Trusted Payment Experience (Dark Trust Box) */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="bg-[#0b1120] rounded-3xl border border-slate-800 shadow-2xl p-6 sm:p-8 relative overflow-hidden text-slate-100">
          <div className="absolute top-0 right-0 w-48 h-48 bg-[radial-gradient(circle,rgba(14,165,233,0.12)_0%,transparent_70%)] pointer-events-none rounded-full" aria-hidden />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[radial-gradient(circle,rgba(236,72,153,0.08)_0%,transparent_70%)] pointer-events-none rounded-full" aria-hidden />

          <div className="candor-trust-panel relative z-10">
            <div className="candor-trust-panel__main">
              <div className="candor-trust-panel__header">
                <div className="w-11 h-11 bg-sky-500/10 rounded-xl flex items-center justify-center border border-sky-500/20 shrink-0">
                  <span className="text-xl" aria-hidden>🛡️</span>
                </div>
                <h3 className="text-xl font-bold tracking-tight text-white m-0">{TRUSTED_PAYMENT.title}</h3>
              </div>

              <div className="candor-trust-panel__copy">
                <p>{TRUSTED_PAYMENT.body}</p>
                <p>{TRUSTED_PAYMENT.bodyExtended}</p>
                <p>{TRUSTED_PAYMENT.ideNote}</p>
              </div>

              <div className="candor-trust-badges">
                <div className="candor-trust-badge">
                  <div className="w-9 h-9 bg-sky-500/10 border border-sky-500/20 rounded-lg flex items-center justify-center shrink-0">
                    <span aria-hidden>🌍</span>
                  </div>
                  <div>
                    <strong>Global Tax Compliance</strong>
                    <span>VAT/GST and sales taxes auto-calculated and remitted by Paddle</span>
                  </div>
                </div>
                <div className="candor-trust-badge">
                  <div className="w-9 h-9 bg-pink-500/10 border border-pink-500/20 rounded-lg flex items-center justify-center shrink-0">
                    <span aria-hidden>📞</span>
                  </div>
                  <div>
                    <strong>24/7 Payment Support</strong>
                    <span>Direct payment, invoice, and refund reconciliation support</span>
                  </div>
                </div>
              </div>
            </div>

            <aside className="candor-trust-help">
              <div className="w-10 h-10 bg-white/[0.06] rounded-full flex items-center justify-center mb-2">
                <span className="text-lg" aria-hidden>❓</span>
              </div>
              <h4 className="text-base font-bold text-white m-0 mb-1">{TRUSTED_PAYMENT.needHelpTitle}</h4>
              <p>{TRUSTED_PAYMENT.needHelpBody}</p>
              <a
                href={`mailto:${SALES_EMAIL}`}
                className="text-sky-400 hover:text-sky-300 font-bold text-sm"
              >
                {SALES_EMAIL}
              </a>
            </aside>
          </div>
        </div>
      </div>

      <CompliancePolicyModal
        activePolicyId={activeComplianceModal}
        onClose={() => setActiveComplianceModal(null)}
      />

      {/* SECTION Early Access Popups */}
      {showEarlyAccessModal && (
        <>
          <div onClick={() => setShowEarlyAccessModal(false)} className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] transition-opacity"></div>
          <div className="fixed inset-0 flex items-center justify-center p-4 z-[201] animate-fade-in pointer-events-none">
            <div className="bg-white rounded-3xl border border-slate-100 shadow-2xl p-6 max-w-sm text-center pointer-events-auto">
              <span className="text-4xl block mb-3">⭐</span>
              <h3 className="text-lg font-extrabold text-slate-900 mb-2">{earlyAccessTier} Early Access</h3>
              <p className="text-xs text-slate-500 leading-relaxed mb-6">
                The {earlyAccessTier} tier is currently in selective private beta for specific enterprise teams. You can subscribe to the Professional tier today inside your IDE or contact us for an early onboarding slot.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowEarlyAccessModal(false);
                    handlePlanChange(
                      groupPlansForPricingGrid(plansList).developerTiers.find(
                        (p) => developerTierKey(p) === 'basic'
                      )?.slug || 'pro_plan'
                    );
                  }}
                  className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs transition shadow"
                >
                  Get Basic Plan
                </button>
                <button
                  onClick={() => {
                    setShowEarlyAccessModal(false);
                    openContactSales({ subject: `${earlyAccessTier} — Early access inquiry` });
                  }}
                  className="flex-1 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition border border-slate-200"
                >
                  Email Sales
                </button>
              </div>
            </div>
          </div>
        </>
      )}


      {/* Cancel Subscription Modal */}
      <CancelModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={confirmCancelSubscription}
        subscription={subscription}
        isCancelling={loading.cancel}
      />

      {/* Plan Switch Warning Modal */}
      <PlanSwitchWarningModal
        isOpen={showPlanSwitchModal}
        onClose={() => {
          setShowPlanSwitchModal(false);
          setPendingPlanId(null);
        }}
        onConfirm={confirmPlanSwitch}
        message={PLAN_SWITCH_WARNING}
      />

    </div>
  );
};

export default SubscriptionPlans;
